// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useEffect, useState } from 'react';

import { copyText, formatAngle, formatLength, headline, summarize, type MeasureState, type MeasureTool } from '../../bygning/ruler';
import { IconButton, XIcon } from '../IconButton';
import { TextLink } from '../TextLink';
import styles from './MeasurePanel.module.css';

export interface MeasurePanelProps {
  state: MeasureState;
  /** The tool on now (null: the panel only lists what was measured). */
  tool: MeasureTool;
  /** The locked plan: the hint says lengths are in the plan. */
  plan: boolean;
  onRemove: (id: string) => void;
  onClear: () => void;
}

const KIND = { distance: 'Afstand', area: 'Areal' } as const;

/** How to work the tool, by what is being drawn. */
function hintFor(tool: MeasureTool, drafting: boolean, plan: boolean): string | null {
  if (tool === null) return null;
  const where = plan ? 'i planen' : 'i modellen';
  if (!drafting) return tool === 'distance' ? `Klik punkter ${where} for at måle en afstand.` : `Klik hjørnerne ${where} for at måle et areal.`;
  return 'Dobbeltklik eller Enter afslutter · Backspace fjerner sidste punkt · Esc annullerer';
}

/**
 * Målinger: what the Ground's Mål afstand / Mål areal tools measured this
 * session (frontend-aa2), under the tools — each with its value, Kopiér and
 * remove, and "Ryd målinger". Presentational: the state and its actions
 * are the host's (`useMeasureTool`).
 */
export function MeasurePanel({ state, tool, plan, onRemove, onClear }: MeasurePanelProps) {
  const [copied, setCopied] = useState<string | null>(null);
  useEffect(() => {
    if (copied === null) return;
    const timer = setTimeout(() => setCopied(null), 1600);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = (id: string, text: string) => {
    const done = () => setCopied(id);
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(text).then(done, () => setCopied(null));
    }
  };

  const hint = hintFor(tool, state.draft !== null, plan);
  const counts = { distance: 0, area: 0 };

  return (
    <section className={styles.panel} aria-label="Målinger">
      <header className={styles.head}>
        <h2 className={styles.title}>Målinger</h2>
        {state.items.length > 0 && (
          <TextLink size="sm" onClick={onClear}>
            Ryd målinger
          </TextLink>
        )}
      </header>
      {hint && <p className={styles.hint}>{hint}</p>}
      {state.items.length > 0 && (
        <ol className={styles.list}>
          {state.items.map((m) => {
            counts[m.kind] += 1;
            const s = summarize(m);
            const detail =
              m.kind === 'distance'
                ? s.segments.length > 1
                  ? `${s.segments.length} stykker`
                  : m.plan
                    ? 'i planen'
                    : '3D'
                : s.selfIntersecting
                  ? (s.warning ?? '')
                  : [`omkreds ${formatLength(s.length)}`, ...(s.tiltDeg !== null ? [`hældning ${formatAngle(s.tiltDeg)}`] : [])].join(' · ');
            const text = copyText(m);
            return (
              <li key={m.id} className={styles.row}>
                <span className={styles.name}>
                  {KIND[m.kind]} {counts[m.kind]}
                </span>
                <span className={styles.value}>{headline(m)}</span>
                <span className={styles.detail}>
                  {detail}
                  {!s.selfIntersecting && s.warning && <span className={styles.warning}> · {s.warning}</span>}
                </span>
                <span className={styles.actions}>
                  <button
                    type="button"
                    className={styles.copy}
                    onClick={() => copy(m.id, text)}
                    title={`Kopiér "${text}"`}
                    aria-label={`Kopiér ${KIND[m.kind].toLowerCase()} ${counts[m.kind]}`}
                  >
                    {copied === m.id ? 'Kopieret' : 'Kopiér'}
                  </button>
                  <IconButton variant="ghost" size="sm" label={`Fjern ${KIND[m.kind].toLowerCase()} ${counts[m.kind]}`} onClick={() => onRemove(m.id)}>
                    <XIcon />
                  </IconButton>
                </span>
              </li>
            );
          })}
        </ol>
      )}
      {state.items.length === 0 && !hint && <p className={styles.hint}>Ingen målinger endnu.</p>}
    </section>
  );
}
